import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { TruncatedTooltipValue } from '@/components/layout/TruncatedTooltipValue';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl/Translation';
import { DeleteIcon, EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { PointConfigurationData } from '@/services/pointConfiguration/pointConfiguration.types';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { Button } from 'ui/button';
import ManagePointCategoryContainer from '../../point-setup-forms/ManagePointCategoryContainer';
import { getEncryptedPointAgentType, PointAgentTab } from '../pointConfiguration.constants';

type ModalSize = 'sm' | 'md' | 'lg';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  size?: ModalSize;
  variant?: 'default' | 'destructive' | 'warning';
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

enum status {
  ACTIVE = 'A',
}

export function getColumns(
  onOpen: OnOpenProps,
  activeTab: PointAgentTab,
  updateStatus: (payload: { pointCategoryId: string; agentType: string; status: 'A' | 'B' }) => Promise<unknown>,
  deleteCategory: (payload: { pointCategoryId: string; agentType: string }) => Promise<unknown>,
  openConfirmation: OpenConfirmationProps,
  t: ReturnType<typeof useDynamicLocale>['t'],
  canViewSlabList: boolean,
): ColumnDef<PointConfigurationData>[] {
  const agentType = getEncryptedPointAgentType(activeTab);
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="max-w-[10rem] truncate text-caption font-medium">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'categoryName',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('pointManagement.pointsConfiguration.customer.table.categoryName')}
        />
      ),
      cell: ({ row }) =>
        row.original.status === status.ACTIVE && canViewSlabList ? (
          <Link
            to="/points-management/points-configuration/affiliate/$pointCategoryId/slabs"
            params={{ pointCategoryId: row.original.pointCategoryId }}
            search={{
              activeTab,
              categoryName: row.original.categoryName,
              page: 0,
              fetch: 10,
            }}
          >
            <TruncatedTooltipValue
              content={row.getValue<string>('categoryName')}
              className="max-w-[20rem] text-info font-medium underline cursor-pointer"
            />
          </Link>
        ) : (
          <TruncatedTooltipValue
            content={row.getValue<string>('categoryName')}
            className="max-w-[20rem] text-muted-foreground font-medium underline cursor-not-allowed"
          />
        ),
      meta: {
        label: t('common.page.tableColumn.categoryName'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'categoryType',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.categoryType')} />
      ),
      cell: () => (
        <div className="max-w-[20rem] truncate text-caption font-medium">{t('common.page.tableColumn.default')}</div>
      ),
      meta: {
        label: t('common.page.tableColumn.categoryType'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.description')} />
      ),
      cell: ({ row }) => (
        <TruncatedTooltipValue
          content={row.getValue<string>('description')}
          className="max-w-[20rem] text-caption font-medium"
        />
      ),
      meta: {
        label: t('common.page.tableColumn.description'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'createdBy',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdBy')} />,
      cell: ({ row }) => <div className="text-[12px] font-[500]">{row.getValue('createdBy')} </div>,
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption font-medium">{formatDate(Number(row.getValue('createdDate')))} </div>
      ),
      meta: {
        label: t('common.page.tableColumn.createdDate'),
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) =>
        row.original.status === status.ACTIVE ? (
          <div className="text-success text-caption font-medium">{t('common.page.table.active')}</div>
        ) : (
          <div className="text-destructive-text text-caption font-medium">{t('common.page.table.blocked')}</div>
        ),

      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const isActive = row.original.status === status.ACTIVE;
        const isDefault = row.original.isDefault;
        return (
          <div className="flex gap-4 items-center">
            {isActive && !isDefault && (
              <Can rule={{ allOf: [PERMS.point.management.update, PERMS.point.management.details] }}>
                <EditIcon
                  className="text-foreground-subtle size-4 hover:cursor-pointer"
                  onClick={() =>
                    onOpen({
                      title: t('pointManagement.page.form.managePointCategory.title'),
                      okText: t('common.page.button.submit'),
                      size: 'lg',
                      modalTitle: 'pointManagement.page.form.managePointCategory.title',
                      component: (
                        <ManagePointCategoryContainer
                          activeTab={activeTab}
                          pointCategoryId={row.original.pointCategoryId}
                        />
                      ),
                    })
                  }
                />
              </Can>
            )}
            {!isDefault && (
              <Can rule={{ allOf: [PERMS.point.management.statusUpdate] }}>
                <Button
                  variant="ghost"
                  className="p-0"
                  onClick={() => {
                    openConfirmation({
                      title: t('common.page.modal.confirm'),
                      description: isActive
                        ? t('common.page.modal.blockMessage')
                        : t('common.page.modal.unblockMessage'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.submit'),
                      size: 'lg',
                      variant: 'warning',
                      onAction: async () => {
                        await updateStatus({
                          pointCategoryId: row.original.pointCategoryId,
                          agentType,
                          status: isActive ? 'B' : 'A',
                        });
                      },
                      onCancel: () => {},
                    });
                  }}
                >
                  {row.original.status === 'A' ? (
                    <LockCloseIcon className="text-destructive-text size-4" />
                  ) : (
                    <LockOpenIcon className="text-link size-4" />
                  )}
                </Button>
              </Can>
            )}
            {isActive && !isDefault && (
              <Can rule={{ allOf: [PERMS.point.management.delete] }}>
                <Button
                  variant="ghost"
                  className="p-0"
                  onClick={() =>
                    openConfirmation({
                      title: t('common.page.modal.confirm'),
                      description: t('common.page.modal.deleteItemMessage'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.submit'),
                      size: 'lg',
                      variant: 'destructive',
                      onAction: async () => {
                        await deleteCategory({ pointCategoryId: row.original.pointCategoryId, agentType });
                      },
                      onCancel: () => {},
                    })
                  }
                >
                  <DeleteIcon className="size-4 text-destructive-text" />
                </Button>
              </Can>
            )}
          </div>
        );
      },
      size: 40,
    },
  ];
}
